import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { Site } from '../api/types.js'
import { ObjectPicker } from '../components/objects.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner } from '../components/ui.js'
import { addDays, formatDate, formatDateTime, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'

/** Quick ranges, because "last month" is what actually gets asked for. */
function presets(today: string) {
  const [year, month] = today.split('-').map(Number) as [number, number]
  const firstOfThisMonth = `${year}-${String(month).padStart(2, '0')}-01`
  const lastMonthEnd = addDays(firstOfThisMonth, -1)
  const lastMonthStart = `${lastMonthEnd.slice(0, 7)}-01`
  return [
    { label: 'Letzte 30 Tage', from: addDays(today, -29), to: today },
    { label: 'Dieser Monat', from: firstOfThisMonth, to: today },
    { label: 'Letzter Monat', from: lastMonthStart, to: lastMonthEnd },
    { label: 'Dieses Jahr', from: `${year}-01-01`, to: today },
  ]
}

export function ReportsPage() {
  const today = todayIso()
  const [searchParams] = useSearchParams()
  const [objectId, setObjectId] = useState(searchParams.get('objectId') ?? '')
  const [from, setFrom] = useState(addDays(today, -29))
  const [to, setTo] = useState(today)
  const [includePhotos, setIncludePhotos] = useState(true)

  const { data: sites, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api<Site[]>('/api/objects/sites'),
  })

  const query = new URLSearchParams({ objectId, from, to, includePhotos: String(includePhotos) })
  const ready = Boolean(objectId) && to >= from

  return (
    <>
      <PageHead
        title="Nachweise"
        subtitle="Das Dokument, das Sie dem Eigentümer oder der WEG-Versammlung vorlegen."
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <div
          className="report-layout"
          style={{ display: 'grid', gap: 18, gridTemplateColumns: 'minmax(320px, 460px) minmax(0, 1fr)', alignItems: 'start' }}
        >
          <section className="card">
            <div className="card-head">
              <h2>Nachweis erstellen</h2>
            </div>
            <div className="card-body">
              <Field label="Liegenschaft oder Objekt" hint="Meist die Liegenschaft; ein Gebäude oder eine Einheit ergibt den Nachweis nur für diesen Teil.">
                <ObjectPicker value={objectId} onChange={(id) => setObjectId(id)} />
              </Field>

              <div className="field">
                <span>Zeitraum</span>
                <div className="row" style={{ marginBottom: 10 }}>
                  {presets(today).map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className="btn secondary small"
                      style={{ flex: '0 0 auto' }}
                      onClick={() => {
                        setFrom(preset.from)
                        setTo(preset.to)
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <div className="row">
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
                {to < from && <div className="error">Der Zeitraum ist ungültig.</div>}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
                <input
                  type="checkbox"
                  style={{ width: 18, height: 18 }}
                  checked={includePhotos}
                  onChange={(e) => setIncludePhotos(e.target.checked)}
                />
                Fotodokumentation anhängen
              </label>

              <div className="row">
                <a
                  className={`btn ${ready ? '' : 'secondary'}`}
                  href={ready ? `/api/reports/proof?${query}` : undefined}
                  aria-disabled={!ready}
                  onClick={(e) => !ready && e.preventDefault()}
                >
                  <Icon name="download" /> PDF herunterladen
                </a>
                <a
                  className="btn secondary"
                  href={ready ? `/api/reports/proof?${query}&format=html` : undefined}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={!ready}
                  onClick={(e) => !ready && e.preventDefault()}
                >
                  <Icon name="search" /> Vorschau ansehen
                </a>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2>Was im Nachweis steht</h2>
            </div>
            <div className="card-body stack">
              <Notice kind="info">
                Zeitraum {formatDate(from)} bis {formatDate(to)}
                {objectId ? '' : ', noch kein Objekt gewählt'}
              </Notice>
              <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
                <li>
                  <strong>Deckblatt</strong> mit Liegenschaft, Eigentümer, Zeitraum und Erfüllungsquote
                </li>
                <li>
                  <strong>Zusammenfassung</strong> je Leistung: geplant, erledigt, nicht erledigt, entfallen
                </li>
                <li>
                  <strong>Einzelnachweis</strong> chronologisch mit Datum, Uhrzeit, Objekt, Textbaustein und ausführender Person
                </li>
                <li>
                  <strong>Fotodokumentation</strong> mit Datum und Zuordnung
                </li>
              </ol>
              <Notice kind="warn">
                Nicht erledigte Leistungen erscheinen rot im Nachweis, endgültig geschlossene mit Grund. Erledigungen, die die
                Verwaltung nachträglich eingetragen hat, sind ausdrücklich als Nachtrag gekennzeichnet, das ist es, was das
                Dokument gegenüber einem Eigentümer belastbar macht.
              </Notice>
            </div>
          </section>
        </div>
      )}
      <OwnerLinks sites={sites ?? []} defaultObjectId={objectId} />
    </>
  )
}

interface OwnerLink {
  id: string
  label: string | null
  objectId: string
  objectName: string
  siteName: string
  customerName: string | null
  fromDate: string | null
  toDate: string | null
  rollingDays: number | null
  includePhotos: boolean
  showStaffNames: boolean
  expiresAt: string
  revokedAt: string | null
  viewCount: number
  lastViewedAt: string | null
}

/**
 * Instead of e-mailing a PDF after every period, the Verwalter hands the owner
 * one link that always shows the current state. Anyone with the link can read
 * it, so every link expires and can be revoked.
 */
function OwnerLinks({ sites, defaultObjectId }: { sites: Site[]; defaultObjectId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showNew, setShowNew] = useState(false)
  const [linkObjectId, setLinkObjectId] = useState(defaultObjectId)
  const [created, setCreated] = useState<{ url: string; objectName: string; expiresAt: string } | null>(null)

  const { data } = useQuery({ queryKey: ['report-links'], queryFn: () => api<OwnerLink[]>('/api/reports/links') })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ url: string; objectName: string; expiresAt: string }>('/api/reports/links', { method: 'POST', body }),
    onSuccess: async (result) => {
      setShowNew(false)
      setCreated(result)
      await queryClient.invalidateQueries({ queryKey: ['report-links'] })
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Anlegen fehlgeschlagen.', 'error'),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/reports/links/${id}/revoke`, { method: 'POST' }),
    onSuccess: async () => {
      toast('Link gesperrt. Er lässt sich nicht mehr öffnen.')
      await queryClient.invalidateQueries({ queryKey: ['report-links'] })
    },
  })

  const active = (data ?? []).filter((link) => !link.revokedAt && Date.parse(link.expiresAt) > Date.now())
  const inactive = (data ?? []).filter((link) => link.revokedAt || Date.parse(link.expiresAt) <= Date.now())

  return (
    <section className="card" style={{ marginTop: 22 }}>
      <div className="card-head">
        <h2>Eigentümer-Links</h2>
        <button
          type="button"
          className="btn secondary small"
          onClick={() => {
            setLinkObjectId(defaultObjectId)
            setShowNew(true)
          }}
          disabled={sites.length === 0}
        >
          <Icon name="link" size={16} /> Neuen Link erstellen
        </button>
      </div>

      {active.length === 0 && inactive.length === 0 ? (
        <EmptyState
          title="Noch keine Links vergeben"
          hint="Ein Link zeigt dem Eigentümer den Nachweis jederzeit im Browser, ohne dass Sie ein PDF verschicken müssen."
          action={
            <button type="button" className="btn" onClick={() => setShowNew(true)} disabled={sites.length === 0}>
              Neuen Link erstellen
            </button>
          }
        />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Liegenschaft</th>
                <th>Zeitraum</th>
                <th>Inhalt</th>
                <th className="nowrap">Aufrufe</th>
                <th className="nowrap">Gültig bis</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...active, ...inactive].map((link) => {
                const dead = Boolean(link.revokedAt) || Date.parse(link.expiresAt) <= Date.now()
                return (
                  <tr key={link.id} style={dead ? { opacity: 0.5 } : undefined}>
                    <td>
                      <span className="strong">{link.objectName}</span>
                      <div className="muted small">
                        {link.objectName !== link.siteName ? `${link.siteName}, ` : ''}
                        {link.customerName ?? ''}
                        {link.label ? `, ${link.label}` : ''}
                      </div>
                    </td>
                    <td className="small">
                      {link.rollingDays
                        ? `laufend, letzte ${link.rollingDays} Tage`
                        : `${formatDate(link.fromDate!)} bis ${formatDate(link.toDate!)}`}
                    </td>
                    <td className="small">
                      {link.includePhotos ? 'mit Fotos' : 'ohne Fotos'}
                      <div className="muted">{link.showStaffNames ? 'mit Namen' : 'ohne Namen'}</div>
                    </td>
                    <td className="nowrap small">
                      {link.viewCount}
                      {link.lastViewedAt && <div className="muted">{formatDateTime(link.lastViewedAt)}</div>}
                    </td>
                    <td className="nowrap small">
                      {link.revokedAt ? <span className="badge skipped">gesperrt</span> : formatDate(link.expiresAt.slice(0, 10))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {!dead && (
                        <button
                          type="button"
                          className="btn ghost small"
                          onClick={() => {
                            if (confirm('Link sperren? Der Eigentümer kann die Seite danach nicht mehr öffnen.')) {
                              revoke.mutate(link.id)
                            }
                          }}
                        >
                          Sperren
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showNew}
        title="Neuen Eigentümer-Link erstellen"
        onClose={() => setShowNew(false)}
        footer={
          <>
            <button type="button" className="btn secondary" onClick={() => setShowNew(false)}>
              Abbrechen
            </button>
            <button type="submit" form="link-form" className="btn" disabled={create.isPending}>
              Link erstellen
            </button>
          </>
        }
      >
        <form
          id="link-form"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            const form = new FormData(event.currentTarget)
            if (!linkObjectId) {
              toast('Bitte eine Liegenschaft wählen.', 'error')
              return
            }
            create.mutate({
              objectId: linkObjectId,
              label: form.get('label') || undefined,
              mode: 'rolling',
              rollingDays: Number(form.get('rollingDays')),
              includePhotos: form.get('includePhotos') === 'on',
              showStaffNames: form.get('showStaffNames') === 'on',
              expiresInDays: Number(form.get('expiresInDays')),
            })
          }}
        >
          <Field label="Liegenschaft oder Objekt" hint="Der Eigentümer sieht nur diesen Teil und alles darunter.">
            <ObjectPicker value={linkObjectId} onChange={(id) => setLinkObjectId(id)} />
          </Field>
          <Field label="Bezeichnung" hint="Nur für Sie, z. B. „WEG-Beirat Lindenstraße“.">
            <input name="label" />
          </Field>
          <div className="row">
            <Field label="Zeigt die letzten … Tage" hint="Die Seite bleibt dadurch immer aktuell.">
              <input name="rollingDays" type="number" min={7} max={400} defaultValue={90} required />
            </Field>
            <Field label="Gültig für … Tage">
              <input name="expiresInDays" type="number" min={1} max={400} defaultValue={90} required />
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <input type="checkbox" name="includePhotos" defaultChecked style={{ width: 18, height: 18 }} />
            Fotodokumentation anzeigen
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <input type="checkbox" name="showStaffNames" style={{ width: 18, height: 18 }} />
            Namen der Mitarbeiter anzeigen
          </label>
          <Notice kind="warn">
            Wer den Link kennt, kann die Seite ohne Anmeldung öffnen. Die Namen der Mitarbeiter sind deshalb
            standardmäßig ausgeblendet, der Nachweis funktioniert auch ohne sie.
          </Notice>
        </form>
      </Modal>

      <Modal
        open={created !== null}
        title="Link erstellt"
        onClose={() => setCreated(null)}
        footer={
          <button type="button" className="btn" onClick={() => setCreated(null)}>
            Fertig
          </button>
        }
      >
        {created && (
          <>
            <Notice kind="success">
              Der Link für {created.objectName} ist aktiv und gültig bis {formatDate(created.expiresAt.slice(0, 10))}.
            </Notice>
            <Field label="Diesen Link an den Eigentümer weitergeben">
              <input readOnly value={created.url} onFocus={(event) => event.currentTarget.select()} />
            </Field>
            <div className="row">
              <button
                type="button"
                className="btn secondary"
                style={{ flex: '0 0 auto' }}
                onClick={async () => {
                  await navigator.clipboard.writeText(created.url).catch(() => {})
                  toast('Link kopiert.')
                }}
              >
                Link kopieren
              </button>
              <a className="btn secondary" style={{ flex: '0 0 auto' }} href={created.url} target="_blank" rel="noreferrer">
                Ansehen
              </a>
            </div>
          </>
        )}
      </Modal>
    </section>
  )
}
